import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Result, Button, Card, Typography } from 'antd';
import { ReloadOutlined, HomeOutlined, BugOutlined } from '@ant-design/icons';
import { useError } from '../../contexts/ErrorContext';
import { useTheme } from '../ThemeProvider';

const { Text, Paragraph } = Typography;

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundaryClass extends Component<Props & { showError: (message: string) => void; isDark: boolean }, State> {
  constructor(props: Props & { showError: (message: string) => void; isDark: boolean }) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.props.showError('Something went wrong. Please try again.');
    
    this.setState({
      error,
      errorInfo,
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback, isDark } = this.props;

    if (hasError) {
      // Custom fallback UI
      if (fallback) {
        return fallback;
      }

      // Default error UI
      return (
        <div style={{ 
          minHeight: '100vh', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          background: isDark ? '#000000' : '#f5f5f5',
          padding: '20px'
        }}>
          <Card
            style={{
              width: '100%',
              maxWidth: '600px',
              backgroundColor: isDark ? 'rgba(36, 36, 36, 0.8)' : 'rgba(255, 255, 255, 0.9)',
              border: isDark ? '1px solid #434343' : '1px solid #d9d9d9'
            }}
          >
            <Result
              status="error"
              icon={<BugOutlined style={{ color: '#ff4d4f' }} />}
              title="Something Went Wrong"
              subTitle="We're sorry, but something unexpected happened. Please try again."
              extra={[
                <Button 
                  type="primary" 
                  icon={<ReloadOutlined />}
                  key="retry"
                  onClick={this.handleRetry}
                >
                  Try Again
                </Button>,
                <Button 
                  icon={<HomeOutlined />}
                  key="home"
                  onClick={this.handleGoHome}
                >
                  Go Home
                </Button>
              ]}
            />
            
            {process.env.NODE_ENV === 'development' && error && (
              <div style={{ marginTop: '20px', padding: '16px', backgroundColor: isDark ? '#1f1f1f' : '#f6f6f6', borderRadius: '6px' }}>
                <Text strong style={{ color: isDark ? '#fff' : '#000' }}>Error Details (Development):</Text>
                <Paragraph 
                  style={{ 
                    marginTop: '8px', 
                    fontFamily: 'monospace', 
                    fontSize: '12px',
                    color: isDark ? '#ccc' : '#666',
                    wordBreak: 'break-word'
                  }}
                >
                  {error.toString()}
                </Paragraph>
                {errorInfo && (
                  <Paragraph 
                    style={{ 
                      fontFamily: 'monospace', 
                      fontSize: '12px',
                      color: isDark ? '#ccc' : '#666',
                      wordBreak: 'break-word'
                    }}
                  >
                    {errorInfo.componentStack}
                  </Paragraph>
                )}
              </div>
            )}
          </Card>
        </div>
      );
    }

    return children;
  }
}

// Wrapper component to provide context
const ErrorBoundary: React.FC<Props> = ({ children, fallback }) => {
  const { showError } = useError();
  const { isDark } = useTheme();

  return (
    <ErrorBoundaryClass showError={showError} isDark={isDark} fallback={fallback}>
      {children}
    </ErrorBoundaryClass>
  );
};

export default ErrorBoundary; 